/**
 * The fine face parts at double resolution: the traced reference features (head2.ts) and variants
 * made from them — eyes lifted at the outer corner (phoenix eyes), opened rounder, narrowed; brows
 * swept up like a blade, arched, thinned; a wider nose, a smile. Each variant is the reference's own
 * pixels moved by the same few steps in every view (front, turned, profile), so it keeps the traced
 * lashes, iris, lids and shading — the realistic pixel art finish — and stays in place from every
 * side. Variants are built on first use and kept.
 */
import type { View } from '../sprites';
import { type FineFace, type FineSprite, REF_FACE } from './head2';

export type FineSlot = 'eyes' | 'brows' | 'nose' | 'mouth';

/** The reference part's id (as face.ts's REF_PART). */
const REF = 'ref';

/** One pixel-moving step, applied to each feature (each eye, each brow) of a sprite. */
type Step =
  /** Lift the outer end by `n` pixels (negative: lower it), rising steadily from the inner end. */
  | { tilt: number }
  /** Lift only the outer corner by `n` pixels (negative: lower it), the iris staying put. */
  | { corner: number }
  /** Lift the middle by `n` pixels, the ends staying (an arch). */
  | { arch: number }
  /** Repeat the row `at` of the way down (0 top, 1 bottom), making it a pixel taller. */
  | { addRow: number }
  /** Take out the row `at` of the way down. */
  | { dropRow: number }
  /** Repeat the column `at` of the way from the inner end to the outer, making it a pixel wider. */
  | { addCol: number }
  /** Lift both ends of a centred feature (a mouth) by `n` pixels (negative: lower them). */
  | { ends: number };

interface Variant {
  id: string;
  label: string;
  steps: readonly Step[];
}

/** The variants of each slot, the reference first. */
const VARIANTS: Record<FineSlot, readonly Variant[]> = {
  eyes: [
    { id: REF, label: 'ต้นแบบ', steps: [] },
    { id: 'phoenix', label: 'ตาหงส์', steps: [{ dropRow: 0.55 }, { addCol: 0.85 }, { corner: 2 }] },
    { id: 'round', label: 'ตากลมโต', steps: [{ addRow: 0.5 }, { addCol: 0.5 }] },
    { id: 'narrow', label: 'ตาเรียวคม', steps: [{ dropRow: 0.55 }, { addCol: 0.6 }, { corner: 1 }] },
    { id: 'gentle', label: 'ตาอ่อนโยน', steps: [{ addRow: 0.5 }, { corner: -2 }] },
    { id: 'fierce', label: 'ตาดุ', steps: [{ dropRow: 0.2 }, { corner: 1 }] },
  ],
  brows: [
    { id: REF, label: 'ต้นแบบ', steps: [] },
    { id: 'sword', label: 'คิ้วดาบ', steps: [{ addRow: 0 }, { tilt: 2 }] },
    { id: 'arch', label: 'คิ้วโก่ง', steps: [{ arch: 2 }] },
    { id: 'thin', label: 'คิ้วเรียวบาง', steps: [{ dropRow: 1 }] },
    { id: 'thick', label: 'คิ้วหนา', steps: [{ addRow: 0.5 }] },
    { id: 'worried', label: 'คิ้วตก', steps: [{ tilt: -2 }] },
  ],
  nose: [
    { id: REF, label: 'ต้นแบบ', steps: [] },
    { id: 'high', label: 'จมูกโด่ง', steps: [{ addRow: 0 }] },
    { id: 'broad', label: 'จมูกกว้าง', steps: [{ addCol: 0.5 }] },
    { id: 'small', label: 'จมูกเล็ก', steps: [{ dropRow: 0 }] },
  ],
  mouth: [
    { id: REF, label: 'ต้นแบบ', steps: [] },
    { id: 'wide', label: 'ปากกว้าง', steps: [{ addCol: 0.5 }] },
    { id: 'smile', label: 'ยิ้มบาง', steps: [{ addCol: 0.5 }, { ends: 1 }] },
    { id: 'full', label: 'ปากอิ่ม', steps: [{ addRow: 1 }] },
    { id: 'stern', label: 'ปากเม้ม', steps: [{ addCol: 0.5 }, { ends: -1 }] },
  ],
};

/** The parts a slot offers, for pickers. */
export const FINE_PARTS: Record<FineSlot, readonly { id: string; label: string }[]> = {
  eyes: VARIANTS.eyes,
  brows: VARIANTS.brows,
  nose: VARIANTS.nose,
  mouth: VARIANTS.mouth,
};

/** Whether `id` is one of the slot's full-resolution parts. */
export const isFinePart = (slot: FineSlot, id: string): boolean => VARIANTS[slot].some((v) => v.id === id);

type Px = { x: number; y: number; ch: string };

/** A sprite's pixels, placed on the head joint (double-resolution pixels). */
function pixels(sp: FineSprite): Px[] {
  const out: Px[] = [];
  sp.rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++)
      if (row[c] !== '.') out.push({ x: sp.x + c, y: sp.y + r, ch: row[c]! });
  });
  return out;
}

function sprite(px: readonly Px[], palette: readonly string[]): FineSprite {
  const x0 = Math.min(...px.map((p) => p.x));
  const y0 = Math.min(...px.map((p) => p.y));
  const w = Math.max(...px.map((p) => p.x)) - x0 + 1;
  const h = Math.max(...px.map((p) => p.y)) - y0 + 1;
  const rows = Array.from({ length: h }, () => Array<string>(w).fill('.'));
  for (const p of px) rows[p.y - y0]![p.x - x0] = p.ch;
  return { x: x0, y: y0, palette, rows: rows.map((r) => r.join('')) };
}

/**
 * A sprite's features: runs of columns apart from each other (two eyes, two brows; one in profile
 * or for a mouth), each with the side its outer end is on (−1 left, 1 right; 0 for a centred one).
 */
function features(px: readonly Px[], centred: boolean, view: View): { px: Px[]; out: number }[] {
  const xs = [...new Set(px.map((p) => p.x))].sort((a, b) => a - b);
  const runs: number[][] = [];
  for (const x of xs) {
    const run = runs[runs.length - 1];
    if (run && x - run[run.length - 1]! <= 2) run.push(x);
    else runs.push([x]);
  }
  return runs.map((run, i) => ({
    px: px.filter((p) => p.x >= run[0]! && p.x <= run[run.length - 1]!),
    // Two features: each one's outer end is away from the other. One alone: centred (a mouth),
    // or in profile the eye's outer end is toward the ear, behind (left).
    out: centred ? 0 : runs.length > 1 ? (i === 0 ? -1 : 1) : view === 'side' ? -1 : 1,
  }));
}

/** One step applied to one feature. */
function step(f: { px: Px[]; out: number }, s: Step): Px[] {
  const xs = f.px.map((p) => p.x);
  const ys = f.px.map((p) => p.y);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const w = Math.max(1, x1 - x0);
  // 0 at the inner end, 1 at the outer (from the middle out, for a centred feature).
  const along = (x: number) =>
    f.out > 0 ? (x - x0) / w : f.out < 0 ? (x1 - x) / w : Math.abs(x - (x0 + x1) / 2) / (w / 2);
  if ('tilt' in s) return f.px.map((p) => ({ ...p, y: p.y - Math.round(s.tilt * along(p.x)) }));
  // The outer corner: the outer third of the eye rises, more toward its tip.
  if ('corner' in s)
    return f.px.map((p) => ({ ...p, y: p.y - Math.round(s.corner * Math.max(0, (along(p.x) - 0.6) / 0.4)) }));
  if ('arch' in s)
    return f.px.map((p) => ({ ...p, y: p.y - Math.round(s.arch * Math.sin(Math.PI * ((p.x - x0) / w))) }));
  if ('ends' in s) return f.px.map((p) => ({ ...p, y: p.y - (along(p.x) > 0.6 ? s.ends : 0) }));
  if ('addRow' in s || 'dropRow' in s) {
    const at = y0 + Math.round(('addRow' in s ? s.addRow : s.dropRow) * (y1 - y0));
    if ('dropRow' in s)
      return y1 === y0
        ? f.px
        : f.px.filter((p) => p.y !== at).map((p) => (p.y > at ? { ...p, y: p.y - 1 } : p));
    return f.px.flatMap((p) =>
      p.y > at ? [{ ...p, y: p.y + 1 }] : p.y === at ? [p, { ...p, y: p.y + 1 }] : [p],
    );
  }
  // A column repeated: everything beyond it, toward the outer end, moves out a pixel.
  const dir = f.out || 1;
  const at = Math.round(dir > 0 ? x0 + s.addCol * (x1 - x0) : x1 - s.addCol * (x1 - x0));
  return f.px.flatMap((p) =>
    (p.x - at) * dir > 0 ? [{ ...p, x: p.x + dir }] : p.x === at ? [p, { ...p, x: p.x + dir }] : [p],
  );
}

function vary(sp: FineSprite, steps: readonly Step[], centred: boolean, view: View): FineSprite {
  if (!steps.length) return sp;
  const parts = features(pixels(sp), centred, view).map((f) => {
    let px = f.px;
    for (const s of steps) px = step({ px, out: f.out }, s);
    return px;
  });
  return sprite(parts.flat(), sp.palette);
}

const CACHE = new Map<string, FineFace>();

/** A view's face parts for the chosen ids, built from the reference (undefined where it has none). */
export function fineFace(view: View, ids: Readonly<Record<FineSlot, string>>): FineFace | undefined {
  const ref = REF_FACE[view];
  if (!ref) return undefined;
  const key = `${view}:${ids.eyes}:${ids.brows}:${ids.nose}:${ids.mouth}`;
  let face = CACHE.get(key);
  if (face) return face;
  const steps = (slot: FineSlot) => VARIANTS[slot].find((v) => v.id === ids[slot])?.steps ?? [];
  // Closed eyes take only the steps that keep a line a line: the tilt and the width.
  const closing = steps('eyes').filter((s) => 'tilt' in s || 'corner' in s || 'addCol' in s);
  face = {
    ...(ref.eyes && { eyes: vary(ref.eyes, steps('eyes'), false, view) }),
    ...(ref.closed && { closed: vary(ref.closed, closing, false, view) }),
    ...(ref.brows && { brows: vary(ref.brows, steps('brows'), false, view) }),
    ...(ref.nose && { nose: vary(ref.nose, steps('nose'), true, view) }),
    ...(ref.mouth && { mouth: vary(ref.mouth, steps('mouth'), true, view) }),
  };
  CACHE.set(key, face);
  return face;
}
